<script lang="ts">
	// UC-44: Cancel item (T19), over Sell (#379).
	//
	// A tap on a sent line of the open order opens it: the one place a sent
	// line changes. How many, from 1 to what remains of the line, and a line
	// of one asks nothing; then one of the four void reasons, which goes at
	// once for a manager signed in, and opens Manager approval (T20) for
	// anyone else. When the cancellation takes the order's last sent item the
	// sheet says so before the reason: the order is voided instead, and the
	// lines not sent yet stay in the cart as a new order.
	//
	// The sheet hides while Manager approval is up and comes back on its
	// Close, with the order as it was.
	//
	// A weighed portion (#917) is one line at qty 1, so it asks no count and
	// is cancelled whole, its grams in its name.

	import Band from '../components/Band.svelte';
	import Button from '../components/Button.svelte';
	import Sheet from '../components/Sheet.svelte';
	import { message, type Language } from '../i18n/messages';
	import { voidReasons, type ReasonKey } from '../till/sell.svelte';
	import { lineName } from '../till/weighed';
	import type { components } from '../api/types';

	type SentLine = components['schemas']['QuotedLineBody'];

	interface Props {
		open: boolean;
		language: Language;
		/** The sent line, as the order holds what remains of it. */
		line: SentLine | null;
		/** How many items the order holds sent, every line counted. */
		sentCount: number;
		/** The one signed in is a manager: a reason cancels at once. */
		isManager: boolean;
		/** The reason in flight, by its key, while a manager's cancellation is written. */
		saving: ReasonKey | null;
		/** Why the last try did not go through, or ''. */
		failure: string;
		onReason: (qty: number, reasonKey: ReasonKey, reason: string) => void;
		onClose: () => void;
		/** Counts each opening, which starts the count at one. */
		opened: number;
	}

	let {
		open,
		language,
		line,
		opened,
		sentCount,
		isManager,
		saving,
		failure,
		onReason,
		onClose,
	}: Props = $props();

	const left = $derived(line?.qty ?? 0);
	// Each time the sheet is opened on a line it starts at one. It keeps the
	// count while Manager approval hides it, and on that sheet's Close.
	let qty = $derived.by(() => {
		void opened;
		return 1;
	});
	const voids = $derived(left > 0 && sentCount - qty <= 0);
	const mods = $derived((line?.modifiers ?? []).map((m) => m.name).join(' · '));
</script>

<Sheet
	{open}
	title={message(language, 'cancelItem')}
	titleTestId="cancel-line-title"
	{onClose}
	closeLabel={message(language, 'sheetClose')}
	closeTestId="cancel-line-close"
	testId="cancel-line"
>
	<div class="body" data-scroll="">
		<div class="line">
			<span class="what" data-testid="cancel-line-what">
				<bdi class="n" dir="ltr">{left}×</bdi>
				{line ? lineName(language, line.name, line.grams) : ''}
			</span>
			{#if mods}
				<span class="mods">{mods}</span>
			{/if}
		</div>
		{#if left > 1}
			<div class="part">
				<span class="label">{message(language, 'cancelHowMany')}</span>
				<div class="count">
					<Button
						testId="cancel-line-less"
						label=""
						ariaLabel={message(language, 'cancelLess')}
						icon="minus"
						square
						tone="secondary"
						size="lg"
						disabled={qty <= 1 || saving !== null}
						onPress={() => (qty = Math.max(1, qty - 1))}
					/>
					<span class="qty" role="status" data-testid="cancel-line-qty"
						><bdi dir="ltr">{qty}</bdi></span
					>
					<Button
						testId="cancel-line-more"
						label=""
						ariaLabel={message(language, 'cancelMore')}
						icon="plus"
						square
						tone="secondary"
						size="lg"
						disabled={qty >= left || saving !== null}
						onPress={() => (qty = Math.min(left, qty + 1))}
					/>
					<span class="of">
						{message(language, 'cancelOf')}
						<bdi class="n" dir="ltr">{left}</bdi>
					</span>
				</div>
			</div>
		{/if}
		{#if voids}
			<Band tone="flag" testId="cancel-line-voids">{message(language, 'cancelVoids')}</Band>
		{/if}
		<div class="part">
			<span class="label">{message(language, 'cancelWhy')}</span>
			<div class="reasons">
				{#each voidReasons as reason (reason.key)}
					{@const words = message(language, reason.words)}
					<Button
						testId="cancel-reason-{reason.key}"
						label={words}
						tone="secondary"
						size="lg"
						state={saving === reason.key ? 'pending' : 'idle'}
						pendingLabel={message(language, 'saving')}
						disabled={saving !== null && saving !== reason.key}
						onPress={() => onReason(qty, reason.key, words)}
					/>
				{/each}
			</div>
			{#if !isManager}
				<span class="hint">{message(language, 'cancelNeedsManager')}</span>
			{/if}
		</div>
		{#if failure}
			<p class="failure" role="alert" data-testid="cancel-line-failure">{failure}</p>
		{/if}
	</div>
</Sheet>

<style>
	.body {
		flex: 1;
		min-block-size: 0;
		overflow-y: auto;
		overflow-x: hidden;
		display: flex;
		flex-direction: column;
		gap: var(--space-7);
	}

	.line {
		display: flex;
		flex-direction: column;
		gap: var(--space-2);
	}

	.what {
		font-size: var(--text-md);
		font-weight: 700;
		color: var(--text-strong);
	}

	.mods,
	.hint {
		font-size: var(--text-sm);
		color: var(--text-body);
	}

	.n {
		font-family: var(--font-mono);
	}

	.part {
		display: flex;
		flex-direction: column;
		gap: var(--space-3);
	}

	.label {
		font-size: var(--text-sm);
		font-weight: 600;
		color: var(--text-strong);
	}

	.count {
		display: flex;
		align-items: center;
		gap: var(--touch-gap);
	}

	.qty {
		min-inline-size: 72px;
		text-align: center;
		font-family: var(--font-mono);
		font-size: var(--text-h4);
		font-weight: 700;
		color: var(--text-strong);
	}

	.of {
		font-size: var(--text-sm);
		color: var(--text-muted);
	}

	.reasons {
		display: flex;
		flex-wrap: wrap;
		gap: var(--touch-gap);
	}

	.failure {
		margin: 0;
		font-size: var(--text-sm);
		font-weight: 600;
		line-height: var(--leading-snug);
		color: var(--ink-flag-deep);
	}
</style>
